import React, { useEffect } from "react"

import { useQuery } from "react-query"
import { useDispatch, useSelector } from "react-redux"
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanClassificationAlert } from "../components/status/KonanClassificationAlert"
import { ClassificationMetrics } from "../features/ClassificationMetrics/ClassificationMetrics"
import { DataDriftView } from "../features/ModelDrift/DataDriftView"
import { setCurrentModel } from "../features/Models"
import { setCurrentProject } from "../features/projects"
import { KonanAPI } from "../services/KonanAPI"
import type { RootState } from "../store/ReduxStore"
import type { Deployment } from "../types/generated/api/Deployment"
import type { ListClassificationModelOutputs } from "../types/generated/api/ListClassificationModelOutputs"
import type { PaginatedModelListCreateList } from "../types/generated/api/PaginatedModelListCreateList"

export function ModelDetails(): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()
  const currentModel = useSelector((state: RootState) => state.models.currentModel)
  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Fetch projects on mount
  const { data: response } = useQuery<AxiosResponse<Deployment>, AxiosError>(
    ["projects"],
    () => KonanAPI.fetchProject(projectId as string),
    {
      enabled: !!projectId,
      onSuccess: ({ data }) => {
        dispatch(setCurrentProject(data))
      },
    },
  )

  // Fetch models on mount, disable refetching in integration screen
  const { data: models } = useQuery<AxiosResponse<PaginatedModelListCreateList>, AxiosError>(
    ["models", projectId, "state"],
    () =>
      KonanAPI.fetchModels({
        projectId: projectId as string,
        ordering: "state",
      }),
    {
      refetchOnWindowFocus: false,
    },
  )

  const { data: activeConfiguration, isLoading: isActiveLoading } = useQuery<
    AxiosResponse<ListClassificationModelOutputs>,
    AxiosError
  >(["activeConfig", currentModel?.uuid], () => KonanAPI.fetchActiveModelConfiguration(currentModel?.uuid as string), {
    enabled: !!currentModel?.uuid && !!currentProject?.uuid && currentProject.type !== "generic",
    retry: false,
  })

  // Get live model UUID on mount to add to context, if there is no live model -> Get first model
  useEffect(() => {
    if (!currentModel?.uuid) {
      if (models?.data?.results && models?.data?.results.length > 0) {
        dispatch(setCurrentModel(models?.data.results[0]))
      }
    }

    if (models?.data?.count === 0 && !currentModel?.uuid) {
      navigate(`/projects/${projectId}/overview`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModel?.uuid, setCurrentModel, models, projectId, response, setCurrentProject])

  return (
    <div className="layoutPadding">
      {currentProject?.type !== "generic" &&
        !location.pathname.endsWith("/classification") &&
        !isActiveLoading &&
        !activeConfiguration &&
        currentModel?.uuid && (
          <Grid marginBottom={2}>
            <KonanClassificationAlert id={projectId as string} />
          </Grid>
        )}

      <Routes>
        {currentProject?.type !== "generic" && (
          <Route key="model-api-requests" path={`classification`} element={<ClassificationMetrics />} />
        )}
        <Route key="data-drift" path={`drift`} element={<DataDriftView />} />
      </Routes>
    </div>
  )
}
